<template>
  <b-card>
    <div>
      <div class="custom-search d-flex align-items-center justify-content-end">
        <div
          class="d-flex flex-column flex-sm-row align-items-center mb-1 justify-content-around"
        >
          <b-form-input
            v-model.lazy="searchTerm"
            placeholder="Search..."
            type="text"
            class="d-inline-block mr-sm-1 mb-1 mb-sm-0"
            @keyup="onSearch"
          />
          <b-button
            class="flex-shrink-0"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            @click="showAttributeModal"
          >
            Add
          </b-button>
        </div>
      </div>

      <vue-good-table
        :line-numbers="true"
        :rows="filteredRows"
        :columns="columns"
        :pagination-options="{ enabled: true, perPage: pageLength }"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'values'">
            <div class="d-flex flex-wrap">
              <b-badge
                v-for="value in props.row.values"
                :key="value.id"
                variant="light-primary"
                class="mr-50 mb-50 value-badge"
              >
                {{ value.value }}
                <feather-icon
                  icon="Edit2Icon"
                  size="12"
                  class="ml-50 cursor-pointer"
                  @click.stop="showValueModal(props.row, value)"
                />
                <feather-icon
                  icon="XIcon"
                  size="12"
                  class="ml-25 cursor-pointer"
                  @click.stop="deleteValue(value.id)"
                />
              </b-badge>
            </div>
          </span>

          <span v-else-if="props.column.field === 'format_status'">
            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="outline-warning"
              class="btn-sm"
              @click="changeStatus(props.row.id)"
            >
              {{ props.row.status ? 'Active' : 'Inactive' }}
            </b-button>
          </span>

          <span v-else-if="props.column.field === 'created_at'">
            {{ formatFnTableDate(props.row.created_at) }}
          </span>

          <span v-else-if="props.column.field === 'action'">
            <b-dropdown variant="link" toggle-class="text-decoration-none" no-caret>
              <template v-slot:button-content>
                <feather-icon icon="MoreVerticalIcon" size="16" class="text-body align-middle mr-25" />
              </template>

              <b-dropdown-item @click="showAttributeModal(props.row)">
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>Edit</span>
              </b-dropdown-item>

              <b-dropdown-item @click="showValueModal(props.row)">
                <feather-icon icon="PlusIcon" class="mr-50" />
                <span>Add Value</span>
              </b-dropdown-item>

              <b-dropdown-item @click="deleteAttribute(props.row.id)">
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>

          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </div>

    <b-modal
      id="modal-attribute-form"
      centered
      :title="attributeForm.id ? 'Edit Attribute' : 'Add Attribute'"
      hide-footer
      @hidden="resetAttributeModal"
      no-close-on-backdrop
    >
      <validation-observer ref="attributeValidationRef">
        <b-form @submit.prevent="submitAttribute">
          <b-row>
            <b-col cols="12">
              <b-form-group label="Name" label-for="attribute-name">
                <validation-provider #default="{ errors }" name="name" vid="name">
                  <b-form-input
                    id="attribute-name"
                    v-model="attributeForm.name"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Attribute Name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group label="Status" label-for="attribute-status">
                <validation-provider #default="{ errors }" name="status" vid="status">
                  <v-select
                    id="attribute-status"
                    v-model="attributeForm.status"
                    :options="statusValueOption"
                    :reduce="option => option.value"
                    label="name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="mr-1"
              >
                Submit
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>

    <b-modal
      id="modal-attribute-value-form"
      centered
      :title="valueForm.id ? 'Edit Attribute Value' : 'Add Attribute Value'"
      hide-footer
      @hidden="resetValueModal"
      no-close-on-backdrop
    >
      <validation-observer ref="valueValidationRef">
        <b-form @submit.prevent="submitValue">
          <b-row>
            <b-col cols="12" class="mb-1">
              <div class="font-weight-bolder">Attribute: {{ valueForm.attribute_name || '-' }}</div>
            </b-col>

            <b-col cols="12">
              <b-form-group label="Value" label-for="attribute-value">
                <validation-provider #default="{ errors }" name="value" vid="value">
                  <b-form-input
                    id="attribute-value"
                    v-model="valueForm.value"
                    :state="errors.length > 0 ? false : null"
                    placeholder="Attribute Value"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="mr-1"
              >
                Submit
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
  </b-card>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BModal,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { VueGoodTable } from 'vue-good-table'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'AttributeView',
  components: {
    BRow,
    BCol,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BBadge,
    BFormGroup,
    BFormInput,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      pageLength: 10,
      searchTerm: '',
      delayTimer: null,
      rows: [],
      columns: [
        { label: 'Name', field: 'name' },
        { label: 'Values', field: 'values', sortable: false },
        { label: 'Status', field: 'format_status', sortable: false },
        { label: 'Created On', field: 'created_at' },
        { label: 'Action', field: 'action', sortable: false },
      ],
      statusValueOption: [
        { name: 'Active', value: true },
        { name: 'Inactive', value: false },
      ],
      attributeForm: {
        id: null,
        name: '',
        status: true,
      },
      valueForm: {
        id: null,
        attribute_id: null,
        attribute_name: '',
        value: '',
      },
    }
  },
  computed: {
    filteredRows() {
      if (!this.searchTerm) return this.rows
      const needle = this.searchTerm.toLowerCase()
      return this.rows.filter(item => {
        const inName = (item.name || '').toLowerCase().includes(needle)
        const inValues = (item.values || []).some(v =>
          (v.value || '').toLowerCase().includes(needle)
        )
        return inName || inValues
      })
    },
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    formatFnTableDate(value) {
      return value ? this.$moment(value).format('MMM Do YYYY, h:mm a') : ''
    },

    onSearch() {
      if (this.delayTimer) {
        clearTimeout(this.delayTimer)
        this.delayTimer = null
      }
      this.delayTimer = setTimeout(() => {}, 300)
    },

    async loadItems() {
      try {
        const response = await this.$api.get('api/attributes')
        this.rows = response?.data?.data || response?.data || []
      } catch (error) {
        this.showError(error)
      }
    },

    showAttributeModal(row = null) {
      this.attributeForm = row
        ? { id: row.id, name: row.name, status: row.status }
        : { id: null, name: '', status: true }

      this.$bvModal.show('modal-attribute-form')
    },

    resetAttributeModal() {
      this.attributeForm = { id: null, name: '', status: true }
    },

    async submitAttribute() {
      this.$refs.attributeValidationRef.validate().then(async success => {
        if (!success) return
        try {
          if (this.attributeForm.id) {
            await this.$api.put(`api/attributes/${this.attributeForm.id}`, this.attributeForm)
            this.showSuccess('Attribute Successfully Updated')
          } else {
            await this.$api.post('api/attributes', this.attributeForm)
            this.showSuccess('Attribute Successfully Created')
          }

          this.$bvModal.hide('modal-attribute-form')
          this.loadItems()
        } catch (error) {
          this.handleValidationError('attributeValidationRef', error)
        }
      })
    },

    async deleteAttribute(id) {
      try {
        await this.$api.delete(`api/attributes/${id}`)
        this.showSuccess('Attribute Successfully Deleted')
        this.loadItems()
      } catch (error) {
        this.showError(error)
      }
    },

    async changeStatus(id) {
      try {
        await this.$api.get(`api/attribute/status/update/${id}`)
        this.showSuccess('Attribute Status Changed')
        this.loadItems()
      } catch (error) {
        this.showError(error)
      }
    },

    showValueModal(attributeRow, valueRow = null) {
      this.valueForm = {
        id: valueRow ? valueRow.id : null,
        attribute_id: attributeRow.id,
        attribute_name: attributeRow.name,
        value: valueRow ? valueRow.value : '',
      }
      this.$bvModal.show('modal-attribute-value-form')
    },

    resetValueModal() {
      this.valueForm = { id: null, attribute_id: null, attribute_name: '', value: '' }
    },

    async submitValue() {
      this.$refs.valueValidationRef.validate().then(async success => {
        if (!success) return
        try {
          if (this.valueForm.id) {
            await this.$api.put(`api/attribute-values/${this.valueForm.id}`, {
              value: this.valueForm.value,
            })
            this.showSuccess('Attribute Value Successfully Updated')
          } else {
            await this.$api.post(`api/attributes/${this.valueForm.attribute_id}/values`, {
              value: this.valueForm.value,
            })
            this.showSuccess('Attribute Value Successfully Created')
          }

          this.$bvModal.hide('modal-attribute-value-form')
          this.loadItems()
        } catch (error) {
          this.handleValidationError('valueValidationRef', error)
        }
      })
    },

    async deleteValue(id) {
      try {
        await this.$api.delete(`api/attribute-values/${id}`)
        this.showSuccess('Attribute Value Successfully Deleted')
        this.loadItems()
      } catch (error) {
        this.showError(error)
      }
    },

    handleValidationError(refName, error) {
      if (error?.response?.data?.errors) {
        this.$refs[refName].setErrors(error.response.data.errors)
      } else {
        this.showError(error)
      }
    },

    showSuccess(text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Success',
          icon: 'CheckIcon',
          variant: 'success',
          text,
        },
      })
    },

    showError(error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Error',
          icon: 'BellIcon',
          variant: 'danger',
          text: error?.response?.data?.message || 'Something went wrong',
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.value-badge {
  display: inline-flex;
  align-items: center;
}
</style>